import { useEffect, useState } from 'react'
import { Link, NavLink, Route, Routes } from 'react-router-dom'
import { LeadsTable } from './LeadsTable'
import { Login } from './Login'
import { FlowFilter } from './FlowFilter'
import { FlowFilterProvider } from './FlowFilterContext'
import { DateRangeProvider } from './DateRangeContext'
import { OverviewSection } from './sections/OverviewSection'
import { FunnelSection } from './sections/FunnelSection'
import { ABTestSection } from './sections/ABTestSection'
import { AcquisitionSection } from './sections/AcquisitionSection'
import { QualitySection } from './sections/QualitySection'
import { OpsSection } from './sections/OpsSection'
import './admin.css'
import './admin-v2.css'

type AuthState = 'checking' | 'authed' | 'unauthed'

const TABS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/analytics', label: 'Overview', end: true },
  { to: '/analytics/funnel', label: 'Funnel' },
  { to: '/analytics/ab', label: 'A/B Test' },
  { to: '/analytics/acquisition', label: 'Acquisition' },
  { to: '/analytics/quality', label: 'Quality' },
  { to: '/analytics/ops', label: 'Operations' },
  { to: '/analytics/leads', label: 'Leads' },
]

export function AdminApp() {
  const [auth, setAuth] = useState<AuthState>('checking')

  useEffect(() => {
    const previous = document.title
    document.title = 'Flow Analytics'
    return () => { document.title = previous }
  }, [])

  useEffect(() => {
    let cancelled = false
    fetch('/api/admin/me', { credentials: 'include' })
      .then((r) => r.json() as Promise<{ authed: boolean }>)
      .then((d) => { if (!cancelled) setAuth(d.authed ? 'authed' : 'unauthed') })
      .catch(() => { if (!cancelled) setAuth('unauthed') })
    return () => { cancelled = true }
  }, [])

  const logout = async () => {
    await fetch('/api/admin/logout', { method: 'POST', credentials: 'include' }).catch(() => {})
    setAuth('unauthed')
  }

  if (auth === 'checking') {
    return <div className="admin-shell"><div className="login-shell"><div className="muted">Indlæser…</div></div></div>
  }

  if (auth === 'unauthed') {
    return <div className="admin-shell"><Login onSuccess={() => setAuth('authed')} /></div>
  }

  return (
    <FlowFilterProvider>
      <DateRangeProvider initial="30d">
        <div className="admin-shell admin-v2">
          <header className="admin-topbar">
            <div className="topbar-left">
              <Link to="/analytics" className="brand">
                <span className="brand-name">Fælles Forsikring Flow <span className="brand-accent">Analytics</span></span>
              </Link>
            </div>
            <div className="topbar-right">
              <FlowFilter />
              <button className="topbar-logout" onClick={logout}>Log ud</button>
            </div>
          </header>

          <nav className="admin-tabs" role="tablist">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                role="tab"
                className={({ isActive }) => `admin-tab ${isActive ? 'active' : ''}`}
              >
                {t.label}
              </NavLink>
            ))}
          </nav>

          <main className="admin-main">
            <Routes>
              <Route index element={<OverviewSection />} />
              <Route path="funnel" element={<FunnelSection />} />
              <Route path="ab" element={<ABTestSection />} />
              <Route path="acquisition" element={<AcquisitionSection />} />
              <Route path="quality" element={<QualitySection />} />
              <Route path="ops" element={<OpsSection />} />
              <Route path="leads" element={<LeadsTable />} />
            </Routes>
          </main>
        </div>
      </DateRangeProvider>
    </FlowFilterProvider>
  )
}
